import { FormSection, FormRow, BigFormRow, MediumFormRow } from 'framework/ui/layout';
import { useShowContext } from 'framework/show';
import { Box, Typography } from '@mui/material';
import LabelStatus from '../../show/shared/LabelStatus';
import TargetHireDateInput from '../../shared/inputs/TargetHireDateInput';
import { EnumDropdown, NumberInput, InputList, ClearAfterUnmountWrapper, TextInput } from 'framework/form';
import VacancyWysiwygInput from '../../../vacancy/shared/input/VacancyWysiwygInput';
import useCommonSkillsService from 'domain/recruiting/candidate/commonskills/commonSkillsService';
import { DateField } from 'framework/ui/field';
import { EmployeeInput } from 'domain/shared/employee';
import { HIRING_MODE, POSITION_STATUS } from 'domain/shared/newVacancy';
import PositionsInputsListWrapper from '../../shared/inputs/positions/PositionsInputsListWrapper';
import { UpdateWorkingConditionsInputListTemplate } from '../../shared/inputs/WorkingConditionsInputs';
import ProjectDescriptionInputs from '../../shared/inputs/ProjectDescriptionInputs';
import { SkillsInput } from 'domain/recruiting/shared/skills';
import { NoteAttachmentUploaderInput } from 'domain/shared/artifact/input';
import { ENGLISH_LEVEL_SHORT_OPTIONS } from 'domain/shared/core/constants';

const UpdateVacancyForm = () => {
  const { searchByName } = useCommonSkillsService();
  const { data: showData } = useShowContext();
  const data = showData.getData();

  return (
    <>
      <FormSection
        titleId="main"
        title={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <Typography variant="h4">
              {data.specializationName} {data.competencyRange}
            </Typography>
            <LabelStatus status={data.status} />
          </Box>
        }
      >
        <FormRow colXl={3}>
          <TextInput label="Title" source="title" isRequired />
          <DateField source="expirationDate" label="Expiration" />
        </FormRow>

        <FormRow colXl={3}>
          <TargetHireDateInput />
          <Box sx={{ mt: 3 }}>
            <EnumDropdown
              tooltip={
                <>
                  Active hiring mode means that a new position is available and the person is needed to fill it.
                  <br />
                  Bar raiser means that no additional positions are available, but the staffing coordinator will replace
                  low-performers with the hired person.
                </>
              }
              options={HIRING_MODE}
              source="hiringMode"
              label="Hiring mode"
              placeholder="Select Mode"
            />
          </Box>
        </FormRow>

        <FormRow colXl={3}>
          <EmployeeInput
            mapResults={(employee: any) => ({
              id: employee.alias,
              text: `${employee.nameEn.firstName} ${employee.nameEn.lastName}`,
            })}
            nameSource="responsiblePersonName"
            source="responsiblePerson"
            label="DRI"
          />
          <EmployeeInput
            mapResults={(employee: any) => ({
              id: employee.alias,
              text: `${employee.nameEn.firstName} ${employee.nameEn.lastName}`,
            })}
            nameSource="staffingCoordinatorName"
            source="staffingCoordinator"
            label="Coordinator"
          />
        </FormRow>
      </FormSection>

      <FormSection title="Positions">
        <BigFormRow>
          <InputList
            maxFields={100}
            addText="Add position"
            minFields={data.positions.length}
            source="positions"
            defaultValue={{
              status: POSITION_STATUS.OPEN,
            }}
            disabledRemove={value => !!value.id}
            inputTemplate={PositionsInputsListWrapper}
          />
        </BigFormRow>
      </FormSection>

      <FormSection title="Working conditions">
        <ClearAfterUnmountWrapper>
          <InputList
            minFields={1}
            source="workingConditions"
            addText="Add location"
            maxFields={10}
            inputTemplate={UpdateWorkingConditionsInputListTemplate}
          />
        </ClearAfterUnmountWrapper>
      </FormSection>

      <FormSection title="Project description">
        <ClearAfterUnmountWrapper>
          <InputList source="projects" addText="Add project" maxFields={10} inputTemplate={ProjectDescriptionInputs} />
        </ClearAfterUnmountWrapper>
      </FormSection>

      <FormSection title="Responsibilities*">
        <BigFormRow>
          <VacancyWysiwygInput source="responsibilities" label="" />
        </BigFormRow>
      </FormSection>

      <FormSection title="Mandatory requirements">
        <BigFormRow>
          <VacancyWysiwygInput source="mandatoryRequirements.description" label="" />
        </BigFormRow>

        <MediumFormRow>
          <SkillsInput
            source="mandatoryRequirements.skills"
            loadSuggestionsMethod={searchByName}
            label="Skills"
            helpText="Required skills for the Vacancy. These skills will be used for Candidate matching purposes and will not be publicly displayed."
          />
        </MediumFormRow>

        <FormRow>
          <NumberInput source="mandatoryRequirements.requiredExperienceInYears" label="Required experience (years)" />

          <EnumDropdown
            source="mandatoryRequirements.englishLevel"
            label="English level"
            options={ENGLISH_LEVEL_SHORT_OPTIONS}
            placeholder="Choose English Level"
          />
        </FormRow>
      </FormSection>

      <FormSection title="Optional requirements">
        <BigFormRow>
          <VacancyWysiwygInput source="optionalRequirements.description" label="" />
        </BigFormRow>

        <MediumFormRow>
          <SkillsInput
            source="optionalRequirements.skills"
            loadSuggestionsMethod={searchByName}
            label="Skills:"
            helpText="Optional skills for the Vacancy. These skills will be used for Candidate matching purposes and will not be publicly displayed."
          />
        </MediumFormRow>
      </FormSection>

      <FormSection title="Notes">
        <BigFormRow>
          <VacancyWysiwygInput source="notes.description" label="" />
        </BigFormRow>
        <FormRow>
          <NoteAttachmentUploaderInput label="Attachments" source="notes.attachmentIds" />
        </FormRow>
      </FormSection>
    </>
  );
};

export default UpdateVacancyForm;
